import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { receiptReturnService } from '@/services/receiptReturnService'
import { financeService } from '@/services/financeService'
import { invoicesService } from '@/services/invoicesService'
import { ReceiptReturnListItem, ReceiptReturnListResponse, FinanceCustomer, InvoiceListItem } from '@/api/types'

export const ReceiptReturnsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [receiptReturns, setReceiptReturns] = useState<ReceiptReturnListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])

  const branchId = user?.selectedBranch?.branchId

  // Load customers and invoices for mapping
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        // Load customers
        const customersData = await financeService.getAllCustomers(branchId)
        setCustomers(customersData || [])

        // Load invoices - try to get all invoices with a large page size
        try {
          const invoicesResponse = await invoicesService.getInvoices(branchId, 1, 1000)
          const invoicesData = invoicesResponse.data || []
          console.log('🔵 [ReceiptReturnsList] Loaded invoices from invoicesService:', invoicesData.length)
          console.log('🔵 [ReceiptReturnsList] Sample invoice:', invoicesData[0])
          setInvoices(invoicesData)
        } catch (invoicesError) {
          console.warn('Failed to load invoices from invoicesService:', invoicesError)
          setInvoices([])
        }
      } catch (error: any) {
        console.error('Failed to load customers/invoices for mapping:', error)
        // Error handled silently - list will still work with IDs
      }
    }

    loadLookups()
  }, [branchId])

  // Helper functions to get names
  const getCustomerName = (customerId: number): string => {
    if (!customerId) return 'N/A'
    const customer = customers.find(c => (c.customerId || c.id) === customerId)
    return customer?.customerName || customer?.customerNameEn || customer?.customerNameAr || `Customer #${customerId}`
  }

  const getInvoiceNo = (invoiceId: number): string => {
    if (!invoiceId) return 'N/A'
    const invoice = invoices.find(inv => {
      const invId = (inv as any).invoiceId || (inv as any).id || (inv as any).refInvoiceId
      return invId === invoiceId
    })
    if (invoice) {
      const invoiceNo = (invoice as any).invoiceNo || (invoice as any).invoiceNumber || (invoice as any).invoiceSerialNo
      if (invoiceNo) {
        return invoiceNo
      }
    }
    return `Invoice #${invoiceId}`
  }

  // Load receipt returns
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadReceiptReturns = async () => {
      try {
        setLoading(true)
        const response: ReceiptReturnListResponse = await receiptReturnService.getAllReceiptReturns(
          branchId!,
          pageNumber,
          pageSize
        )
        setReceiptReturns(response.data || [])
        setTotalCount(response.totalRecords || 0)
        // Calculate total pages
        const calculatedTotalPages = response.totalRecords 
          ? Math.ceil(response.totalRecords / response.pageSize) 
          : 0
        setTotalPages(calculatedTotalPages)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setReceiptReturns([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          await showError(
            t('receiptReturns.errorTitle'),
            error.response?.data?.message || t('receiptReturns.failedToLoadReceiptReturns'),
            5000
          )
        }
      } finally {
        setLoading(false)
      }
    }

    loadReceiptReturns()
  }, [branchId, pageNumber, pageSize])

  const filteredReceiptReturns = useMemo(() => {
    if (!searchQuery.trim()) return receiptReturns
    const query = searchQuery.toLowerCase()
    return receiptReturns.filter((item) => {
      return (
        (item.returnNo || '').toLowerCase().includes(query) ||
        (item.invoiceNo || '').toLowerCase().includes(query) ||
        (item.customerName || '').toLowerCase().includes(query) ||
        String(item.id || '').includes(query)
      )
    })
  }, [receiptReturns, searchQuery])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleAddNew = () => {
    navigate('/receipt-returns/new')
  }

  const handleEdit = (item: ReceiptReturnListItem) => {
    const itemId = item.id
    if (!itemId) {
      toast.error(t('receiptReturns.invalidReceiptReturnId'))
      return
    }
    navigate(`/receipt-returns/${itemId}/edit`)
  }

  const handleDelete = async (item: ReceiptReturnListItem) => {
    const itemId = item.id
    if (!itemId) {
      toast.error(t('receiptReturns.invalidReceiptReturnId'))
      return
    }

    const itemName = item.returnNo || t('receiptReturns.receiptReturnNumbered', { id: itemId })
    const confirmed = await showDeleteConfirm(
      itemName,
      t('receiptReturns.deleteConfirmTitle', { name: itemName })
    )
    if (!confirmed) {
      return
    }

    try {
      setDeletingId(itemId)
      // TODO: Add delete API call when available
      await showSuccess(t('receiptReturns.deletedTitle'), t('receiptReturns.receiptReturnDeletedSuccess'), 3000)
      // Reload list
      // await loadReceiptReturns()
    } catch (error: any) {
      await showError(
        t('receiptReturns.deleteFailedTitle'),
        error.response?.data?.message || t('receiptReturns.failedToDeleteReceiptReturn'),
        5000
      )
    } finally {
      setDeletingId(null)
    }
  }

  const handleCopy = () => {
    const tableData = filteredReceiptReturns.map(item => ({
      Id: item.id || '',
      'Return No': item.returnNo || '',
      'Invoice No': getInvoiceNo(item.invoiceId), // Use helper
      'Customer': getCustomerName(item.customerId), // Use helper
      'Date': item.date || '',
      'Amount': item.amount || 0,
      'Return Type': item.returnType || '',
    }))
    if (tableData.length === 0) {
      toast.error(t('receiptReturns.noDataToCopy'))
      return
    }
    const csv = [
      Object.keys(tableData[0]).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    navigator.clipboard.writeText(csv)
    toast.success(t('receiptReturns.copiedToClipboard'))
  }

  const handleExportCSV = () => {
    const tableData = filteredReceiptReturns.map(item => ({
      Id: item.id || '',
      'Return No': item.returnNo || '',
      'Invoice No': getInvoiceNo(item.invoiceId), // Use helper
      'Customer': getCustomerName(item.customerId), // Use helper
      'Date': item.date || '',
      'Amount': item.amount || 0,
      'Return Type': item.returnType || '',
    }))
    if (tableData.length === 0) {
      toast.error(t('receiptReturns.noDataToExport'))
      return
    }
    const csv = [
      Object.keys(tableData[0]).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `receipt_returns_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('receiptReturns.exportedToCsv'))
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('receiptReturns.title')}
        description={t('receiptReturns.manageReceiptReturns')}
        action={{
          label: t('receiptReturns.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('receiptReturns.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1 sm:flex-initial">
                <Copy className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('receiptReturns.copy')}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} className="flex-1 sm:flex-initial">
                <FileDown className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('receiptReturns.csv')}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader message={t('receiptReturns.loadingReceiptReturns')} />
          ) : filteredReceiptReturns.length === 0 ? (
            <EmptyState
              title={t('receiptReturns.noReceiptReturnsFound')}
              description={
                searchQuery
                  ? t('receiptReturns.tryAdjustingSearch')
                  : t('receiptReturns.getStarted')
              }
              action={
                !searchQuery
                  ? {
                      label: t('receiptReturns.addNewReceiptReturn'),
                      onClick: handleAddNew,
                    }
                  : undefined
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.returnNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.invoiceNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.customer')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.returnType')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[120px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receiptReturns.action')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReceiptReturns.map((item) => (
                      <TableRow
                        key={item.id}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {item.returnNo || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getInvoiceNo(item.invoiceId)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getCustomerName(item.customerId)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(item.date || undefined)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {item.returnType || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(item)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.id}
                              className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {t('receiptReturns.showingEntries', {
                      from: (pageNumber - 1) * pageSize + 1,
                      to: Math.min(pageNumber * pageSize, totalCount),
                      total: totalCount,
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('receiptReturns.pageSize')}:
                    </span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      {t('receiptReturns.pageOf', { current: pageNumber, total: totalPages })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.min(totalPages, prev + 1))}
                      disabled={pageNumber >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

